<template>
  <!-- A rough price guide (S2_Interactive): a slider or two and some choices
       give "from £x", then Get a quote. Display only: no price is stored or
       charged, and the small print says it's a guide. The server render shows
       the starting price. -->
  <SectionBand :heading="section.heading || section.title" :variant="variant" :background="section.background" label="Price guide">
    <div class="s2-est t-card">
      <div class="s2-est__rows">
        <div v-for="(r, i) in rows" :key="i" class="s2-est__row">
          <template v-if="r.kind === 'slider'">
            <label :for="`${uid}-${i}`" class="s2-est__label">{{ r.label }} <strong>{{ values[i] }}{{ r.unit ? ' ' + r.unit : '' }}</strong></label>
            <input :id="`${uid}-${i}`" type="range" :min="r.min" :max="r.max" :step="r.step" v-model.number="values[i]" class="s2-est__range" />
          </template>
          <fieldset v-else class="s2-est__choice">
            <legend class="s2-est__label">{{ r.label }}</legend>
            <label v-for="(o, j) in r.options" :key="j" class="s2-est__opt" :class="{on: values[i] === j}">
              <input type="radio" :name="`${uid}-${i}`" :value="j" v-model.number="values[i]" />{{ o.label }}
            </label>
          </fieldset>
        </div>
      </div>
      <div class="s2-est__result" aria-live="polite">
        <span class="t-eyebrow">{{ section.prefix || 'From' }}</span>
        <strong class="s2-est__price">£{{ formatted }}</strong>
        <a v-if="quoteHref" :href="back(quoteHref)" class="t-btn">{{ buttonLabel }}</a>
        <p class="t-muted s2-est__small">{{ section.disclaimer || "A rough guide. We'll confirm the price when we've seen the job." }}</p>
      </div>
    </div>
  </SectionBand>
</template>

<script setup>
import {computed, reactive, useId, watch} from "vue";
import SectionBand from "../shared/SectionBand.vue";
import {estimatorRows, estimateFrom} from "@/utils/estimator.js";
import {safeLink} from "@/utils/blocks.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();

const props = defineProps({section: {type: Object, required: true}, variant: {type: [Number, String], default: 1}});
const uid = useId();
const rows = computed(() => estimatorRows(props.section.rows));
const values = reactive(rows.value.map((r) => r.start));
// The studio preview can change the rows: start again from their defaults.
watch(rows, (now) => { values.splice(0, values.length, ...now.map((r) => r.start)); });
const total = computed(() => estimateFrom(Number(props.section.base) || 0, rows.value, values));
const formatted = computed(() => total.value.toLocaleString("en-GB", {maximumFractionDigits: 0}));
const buttonLabel = computed(() => (typeof props.section.button === "string" && props.section.button) || props.section.button?.label || "Get an exact quote");
const quoteHref = computed(() => safeLink(props.section.button?.link || props.section.link || "/contact#contact"));
</script>

<style scoped>
.s2-est { display: grid; grid-template-columns: 1.3fr 1fr; gap: clamp(1.25rem, 4vw, 3rem); padding: clamp(1.25rem, 3vw, 2rem); }
.s2-est__rows { display: grid; gap: 1.4rem; }
.s2-est__label { display: flex; justify-content: space-between; font-weight: 600; margin-bottom: .5rem; }
.s2-est__range { width: 100%; accent-color: var(--t-accent); min-height: 44px; }
.s2-est__choice { border: 0; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: .5rem; }
.s2-est__choice legend { width: 100%; }
.s2-est__opt { display: inline-flex; align-items: center; gap: .4rem; border: 1.5px solid var(--t-line); border-radius: 999px; padding: .45rem .9rem; cursor: pointer; min-height: 44px; }
.s2-est__opt input { position: absolute; opacity: 0; width: 1px; height: 1px; }
.s2-est__opt.on { border-color: var(--t-accent); background: color-mix(in srgb, var(--t-accent) 12%, transparent); font-weight: 700; }
.s2-est__opt:focus-within { outline: 3px solid color-mix(in srgb, var(--t-accent) 55%, transparent); outline-offset: 2px; }
.s2-est__result { display: flex; flex-direction: column; gap: .6rem; justify-content: center; }
.s2-est__price { font-family: var(--t-font-display); font-size: clamp(2.4rem, 5vw, 3.6rem); line-height: 1; color: var(--t-accent); }
.s2-est__small { font-size: .82rem; }
@media (max-width: 760px) { .s2-est { grid-template-columns: 1fr; } }
</style>
